import React, { useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Users, Cpu, CheckCircle2, ShieldQuestion, TriangleAlert, Clock, DollarSign, ArrowRight } from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { EmployeePanel } from "@/components/EmployeePanel";
import { useStore } from "@/lib/store";
import { METRICS, DEPARTMENTS, EXCEPTIONS } from "@/data/seed";
import { useNavigate } from "react-router-dom";

function Metric({ icon: Icon, label, value, sub, color = "#00E5FF", testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: `${color}18`, color }}><Icon size={17} /></span>
        {sub && <span className="text-[11px] font-mono text-slate-500">{sub}</span>}
      </div>
      <div className="mt-3 font-display text-3xl font-semibold text-white">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </motion.div>
  );
}

const SEV = { high: "#FF0055", medium: "#FFBF00", low: "#22D3EE" };

export default function MissionControl() {
  const { state } = useStore();
  const [panelId, setPanelId] = useState(null);
  const navigate = useNavigate();
  const pending = state.approvals.filter((a) => a.status === "pending").length;

  return (
    <div>
      {panelId && <EmployeePanel employeeId={panelId} onClose={() => setPanelId(null)} />}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">Mission Control</h1>
        <p className="mt-1 text-sm text-slate-400">Operational overview · <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-mono uppercase text-slate-500">Demo data</span></p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric icon={Users} label="Associates online" value={state.employees.filter(e => e.status !== "off_duty").length} testid="metric-online" />
        <Metric icon={Cpu} label="Tasks in progress" value={METRICS.tasksInProgress} color="#8A2BE2" testid="metric-progress" />
        <Metric icon={CheckCircle2} label="Tasks completed today" value={METRICS.tasksToday} testid="metric-today" />
        <Metric icon={ShieldQuestion} label="Approvals waiting" value={pending} color="#FFBF00" testid="metric-approvals" />
        <Metric icon={TriangleAlert} label="Escalations" value={METRICS.escalations} color="#FF0055" testid="metric-escalations" />
        <Metric icon={Clock} label="Est. hours saved" value={METRICS.hoursSaved} sub="this month" testid="metric-hours" />
        <Metric icon={DollarSign} label="Est. operating cost" value={`$${METRICS.operatingCost.toLocaleString()}`} sub="this month" color="#22D3EE" testid="metric-cost" />
        <Metric icon={CheckCircle2} label="Tasks this month" value={METRICS.tasksMonth} color="#8A2BE2" testid="metric-month" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Live activity feed */}
        <div className="glass rounded-2xl p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-medium text-white">Recent associate activity</h2>
            <button onClick={() => navigate("/activity")} className="flex items-center gap-1 text-xs text-[#00E5FF]" data-testid="mission-view-all-activity">View all <ArrowRight size={13} /></button>
          </div>
          <div className="space-y-2">
            {state.activity.slice(0, 7).map((a) => {
              const emp = state.employees.find((e) => e.id === a.employee);
              const Icon = Icons[a.icon] || Icons.Activity;
              return (
                <div key={a.id} className="flex items-center gap-3 rounded-xl border border-white/6 bg-[#030A16]/40 px-3 py-2.5" data-testid={`feed-${a.id}`}>
                  <span className="font-mono text-xs text-slate-500">{a.time}</span>
                  {emp && <img src={emp.avatar} alt={emp.name} className="h-8 w-8 rounded-lg object-cover" />}
                  <div className="min-w-0 flex-1"><div className="truncate text-sm text-slate-200"><span className="text-white">{emp?.name || a.employee}</span> — {a.task}</div><div className="truncate text-[11px] text-slate-500">{a.result}</div></div>
                  <Icon size={15} className="text-[#00E5FF]" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Work by department + exceptions */}
        <div className="space-y-4">
          <div className="glass rounded-2xl p-5">
            <h2 className="mb-3 font-display text-lg font-medium text-white">Work by department</h2>
            <div className="space-y-3">
              {DEPARTMENTS.filter(d => d.managerId).map((d) => (
                <div key={d.id}>
                  <div className="flex justify-between text-xs"><span className="text-slate-300">{d.name}</span><span className="font-mono text-slate-500">{d.workload}%</span></div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full" style={{ width: `${d.workload}%`, background: d.color }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="glass rounded-2xl p-5">
            <h2 className="mb-3 font-display text-lg font-medium text-white">Important exceptions</h2>
            <div className="space-y-2">
              {EXCEPTIONS.map((ex) => (
                <button key={ex.id} onClick={() => setPanelId(ex.owner)} className="flex w-full items-center gap-3 rounded-xl border border-white/6 bg-[#030A16]/40 px-3 py-2.5 text-left transition-colors hover:border-[#00E5FF]/30" data-testid={`exception-${ex.id}`}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: SEV[ex.severity], boxShadow: `0 0 8px ${SEV[ex.severity]}` }} />
                  <div className="min-w-0 flex-1"><div className="font-mono text-xs text-white">{ex.id}</div><div className="truncate text-[11px] text-slate-400">{ex.site} · {ex.issue}</div></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
